import { isValidDate, parseDate } from './date';
import {
    scrollToMap,
    TAction,
    TInitialState,
    TFilter,
    setBoundsToCountry,
    loadCountriesGeoJSON,
} from '@components';
import { Dispatch } from 'react';
import { health, LAYER_NAMES } from '@constants';
import mapboxgl from 'mapbox-gl';
import { THealthSecurityIncident } from '@types';
import { openIncidentPopupAndFetchDescription } from '@helpers';

// Create urlParameters to prevent race condition caused by simultaneous handling of 'country' and 'popupID' URL parameters
const urlParameters =
    typeof window !== 'undefined' // Check if code is running in the browser
        ? new URLSearchParams(window.location.search) // If in browser, create URLSearchParams object from current URL
        : new URLSearchParams(); // If not in the browser, create an empty URLSearchParams object

export function updateUrlParameters(
    parameters: Record<string, string | undefined>
): void {
    if (typeof window === 'undefined') return;

    // Update parameters
    for (const [key, value] of Object.entries(parameters)) {
        if (value === undefined) {
            urlParameters.delete(key);
        } else {
            urlParameters.set(key, value);
        }
    }

    const search = urlParameters.toString();
    const newUrl = search
        ? `${window.location.pathname}?${search}`
        : window.location.pathname;

    // Safe and allowed everywhere
    window.history.replaceState(undefined, '', newUrl);
}

function getDateFromUrl(dateKey: string): Date | null {
    const dateParameter = urlParameters.get(dateKey);
    const date = parseDate(dateParameter);
    return date;
}

export const hasUrlParameters = (): boolean => {
    const urlParametersString = urlParameters.toString();
    return urlParametersString !== '';
};

export function applyUrlFilters(
    initialState: TInitialState,
    dispatch: Dispatch<TAction>
): void {
    const defaults: TFilter = initialState.filter;

    const parsed: Partial<TFilter> = {};

    const category = urlParameters.get('category');
    const isInOptions = health.filters.categoryOptions.some(
        option => option.value === category
    );
    if (isInOptions) {
        parsed.category = category;
    }

    const fromDate = getDateFromUrl('fromDate');
    if (fromDate && isValidDate(fromDate)) {
        parsed.fromDate = fromDate;
    }

    const toDate = getDateFromUrl('toDate');
    if (
        toDate &&
        isValidDate(toDate) &&
        (!parsed.fromDate || toDate >= parsed.fromDate)
    ) {
        parsed.toDate = toDate;
    }

    dispatch({
        type: 'updateFilters',
        value: {
            ...defaults,
            ...parsed,
        },
    });

    if (hasUrlParameters()) {
        scrollToMap();
    }
}

function parseZoom(zoom: string | undefined): number | undefined {
    if (!zoom) return;

    const parsedZoom = Number(zoom);

    return Number.isNaN(parsedZoom) ? undefined : parsedZoom;
}

function parseLatLng(lat: string, lng: string): [number, number] | undefined {
    if (!lat || !lng) return;
    const latitude = Number(lat);
    const longitude = Number(lng);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) return;
    return [longitude, latitude]; // map.flyTo() takes lngLat
}

export function setMapViewFromURL(map: mapboxgl.Map): void {
    const parsedZoom = parseZoom(urlParameters.get('zoom'));
    const lngLat = parseLatLng(
        urlParameters.get('lat'),
        urlParameters.get('lng')
    );
    if (parsedZoom && lngLat) {
        map.flyTo({
            center: lngLat ? lngLat : map.getCenter(),
            zoom: parsedZoom ? parsedZoom : 1.5,
        });
    }
}

export function setMapViewFromParameters(
    map: mapboxgl.Map,
    lngLat?: [number, number],
    zoom?: number
): void {
    const parsedLngLat = lngLat ? lngLat : map.getCenter();
    const parsedZoom = zoom ? zoom : 1.5;
    map.flyTo({
        center: parsedLngLat,
        zoom: parsedZoom,
    });
}

// This function, and all multipleCountries logic, is an internal feature:
// multi-country selection only updates the map's styling and hides the statistics container.
// It does not update `selectedCountry` state, so subsequent map interactions don't behave correctly.
// Any component watching `selectedCountry` won't react to a multi-country selection.
// To fully develop this into a real feature this, `selectedCountry` has to be changed to an array everywhere
// it's read, and switch existence checks (boolean) to length checks (`selectedCountry.length > 0`).
const handleMultipleCountries = async (
    map: mapboxgl.Map,
    countries: string[]
) => {
    if (countries.length === 0) {
        return;
    }

    map.setPaintProperty(LAYER_NAMES.COUNTRIES, 'fill-color', [
        'case',
        ['in', ['get', 'ISO_A3_EH'], ['literal', countries]],
        'rgba(0, 0, 0, 0)',
        'DarkGray',
    ]);

    map.setFilter(LAYER_NAMES.POINTS, [
        'in',
        ['get', 'countryCode'],
        ['literal', countries],
    ]);
};

// Splits comma-separated params and separates into valid vs invalid entries
const splitAndValidate = (
    parameters: string,
    validValues: Set<string>
): string[] | undefined => {
    const values = parameters ? parameters.split(',') : [];
    const uniqueValues = [...new Set(values)];
    const valid = uniqueValues.filter(value => validValues.has(value));

    console.log('valid:', valid);
    return valid.length > 0 ? valid : undefined;
};

const handleURLCountryParameter = async (
    map: mapboxgl.Map,
    handleSelectCountry: (countryCode: string) => void,
    isSmallScreen: boolean,
    hasMultipleCountries: (value: boolean) => void
) => {
    const countryParameter = urlParameters.get('country');
    if (!countryParameter) {
        return;
    }
    console.log('countryParam', countryParameter);

    const mapCountries = await loadCountriesGeoJSON();
    const features = mapCountries.features;
    const isoCodes = new Set(
        features.map(country => country.properties?.ISO_A3_EH)
    );
    const validCountries = splitAndValidate(countryParameter, isoCodes);
    if (!validCountries) {
        console.log('no valid countries');
        return;
    }

    if (validCountries.length > 1) {
        hasMultipleCountries(true);
        handleMultipleCountries(map, validCountries);
        return;
    }

    const country = validCountries[0];
    const zoom = parseZoom(urlParameters.get('zoom'));
    const lngLat = parseLatLng(
        urlParameters.get('lat'),
        urlParameters.get('lng')
    );
    const hasMapView = zoom && lngLat;
    if (!hasMapView) {
        setBoundsToCountry(map, country, isSmallScreen);
    }
    handleSelectCountry(country);
};

export async function setIncidentPopupAndCountryFromURL(
    map: mapboxgl.Map,
    incidents: THealthSecurityIncident[],
    handleSelectCountry: (countryCode: string) => void,
    isSmallScreen: boolean,
    hasMultipleCountries: (value: boolean) => void
) {
    handleURLCountryParameter(
        map,
        handleSelectCountry,
        isSmallScreen,
        hasMultipleCountries
    );

    const popupID = urlParameters.get('popupID');
    if (Number.isNaN(popupID) || !incidents) return;
    const incident = incidents.find(
        inc => String(inc.incidentNumber) === popupID
    );
    if (!incident) return;
    const incidentID = incident.incidentNumber;

    const coordinates: [number, number] = [
        Number(incident.longitude),
        Number(incident.latitude),
    ];

    openIncidentPopupAndFetchDescription(map, coordinates, incidentID);

    // Remove bordered popup close button because of page load autofocus
    const closeButton = document.querySelector(
        '.mapboxgl-popup-close-button'
    ) as HTMLButtonElement;
    if (closeButton) closeButton.blur();
}
